import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaUpload, FaPlus, FaTrash, FaInfoCircle } from "react-icons/fa";

const VisaApplicationForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { countryId } = useParams();

  const selectedCountry = location.state?.selectedCountry;
  const visaDetails = location.state?.visaDetails;

  const [isLoading, setIsLoading] = useState(false);
  const [primaryApplicant, setPrimaryApplicant] = useState({
    fullName: "",
    passportNumber: "",
  });

  const [familyMembers, setFamilyMembers] = useState([]);
  const [documents, setDocuments] = useState({
    passportFront: null,
    passportBack: null,
    passportCoverBlack: null,
    onwardFlightTicket: null,
    returnFlightTicket: null,
    panCard: null,
    recentPhotograph: null,
    additionalVisaDocument1: null,
    additionalVisaDocument2: null,
    additionalVisaDocument3: null,
    additionalVisaDocument4: null,
  });

  const handleDocChange = (e, docName) => {
    if (e.target.files && e.target.files[0]) {
      setDocuments({ ...documents, [docName]: e.target.files[0] });
    }
  };

  const addFamilyMember = () => {
    setFamilyMembers([...familyMembers, {
      fullName: "",
      passportNumber: "",
      documents: {
        passportFront: null,
        passportBack: null,
        passportCoverBlack: null,
        onwardFlightTicket: null,
        returnFlightTicket: null,
        panCard: null,
        recentPhotograph: null,
        additionalVisaDocument1: null,
        additionalVisaDocument2: null,
        additionalVisaDocument3: null,
        additionalVisaDocument4: null,
      }
    }]);
  };

  const handleFamilyDocChange = (index, e, docName) => {
    if (e.target.files && e.target.files[0]) {
      const updated = [...familyMembers];
      if (!updated[index].documents) updated[index].documents = {};
      updated[index].documents[docName] = e.target.files[0];
      setFamilyMembers(updated);
    }
  };

  const removeFamilyMember = (index) => {
    const updated = familyMembers.filter((_, i) => i !== index);
    setFamilyMembers(updated);
  };

  const handleFamilyChange = (index, field, value) => {
    const updated = [...familyMembers];
    updated[index][field] = value;
    setFamilyMembers(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!primaryApplicant.fullName || !primaryApplicant.passportNumber) {
      toast.error("Please fill primary applicant details");
      return;
    }

    // Check required docs
    const required = ["passportFront", "passportBack", "panCard", "recentPhotograph"];
    for (let req of required) {
      if (!documents[req]) {
        toast.error(`Please upload ${req.replace(/([A-Z])/g, ' $1').trim()}`);
        return;
      }
    }

    const formData = new FormData();
    formData.append("country", selectedCountry?.name || countryId);
    formData.append("visaType", visaDetails?.name || "Standard");
    formData.append("price", visaDetails?.price ? visaDetails.price.replace(/[^0-9]/g, '') : 0);
    formData.append("primaryFullName", primaryApplicant.fullName);
    formData.append("primaryPassportNumber", primaryApplicant.passportNumber);
    formData.append("familyMembers", JSON.stringify(familyMembers));

    Object.keys(documents).forEach((key) => {
      if (documents[key]) {
        formData.append(key, documents[key]);
      }
    });

    familyMembers.forEach((member, index) => {
      if (member.documents) {
        Object.keys(member.documents).forEach(docKey => {
          if (member.documents[docKey]) {
            formData.append(`familyMember_${index}_${docKey}`, member.documents[docKey]);
          }
        });
      }
    });

    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      const url = import.meta.env.VITE_URL || "http://localhost:3000";
      const response = await fetch(`${url}/api/visa/apply`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const data = await response.json();
      if (response.ok) {
        toast.success("Visa application submitted successfully!");
        navigate("/profile"); // Assuming /profile has the My Visas tab
      } else {
        toast.error(data.message || "Failed to submit application");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  const FileUploadBox = ({ title, docKey, required = false, accept = ".jpg,.jpeg,.png,.pdf" }) => (
    <div className="border border-dashed border-gray-300 rounded-xl p-6 flex flex-col items-center justify-center text-center bg-gray-50 hover:bg-gray-100 transition-colors">
      <h4 className="text-sm font-semibold mb-1 text-gray-800">
        {title} {required && <span className="text-red-500">*</span>}
      </h4>
      <p className="text-xs text-gray-500 mb-4">Accepted: {accept}</p>

      <label className="cursor-pointer flex flex-col items-center">
        <div className="w-10 h-10 bg-white shadow flex items-center justify-center rounded-full mb-2 text-blue-500">
          <FaUpload />
        </div>
        <span className="text-sm font-medium text-blue-600 hover:underline">
          {documents[docKey] ? documents[docKey].name : "Click to upload"}
        </span>
        <input
          type="file"
          className="hidden"
          accept={accept}
          onChange={(e) => handleDocChange(e, docKey)}
        />
      </label>
    </div>
  );

  const FamilyFileUploadBox = ({ title, docKey, index, required = false, accept = ".jpg,.jpeg,.png,.pdf" }) => {
    const file = familyMembers[index]?.documents?.[docKey];
    return (
      <div className="border border-dashed border-gray-300 rounded-lg p-4 flex flex-col items-center justify-center text-center bg-white hover:bg-gray-50 transition-colors">
        <h4 className="text-xs font-semibold mb-1 text-gray-800">
          {title} {required && <span className="text-red-500">*</span>}
        </h4>
        <label className="cursor-pointer flex flex-col items-center mt-2">
          <div className="w-8 h-8 bg-gray-100 flex items-center justify-center rounded-full mb-1 text-blue-500">
            <FaUpload size={12} />
          </div>
          <span className="text-xs font-medium text-blue-600 hover:underline truncate max-w-[120px]">
            {file ? file.name : "Upload"}
          </span>
          <input
            type="file"
            className="hidden"
            accept={accept}
            onChange={(e) => handleFamilyDocChange(index, e, docKey)}
          />
        </label>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-blue-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate(-1)} className="text-blue-600 mb-4 font-medium flex items-center gap-2">
          &lt; Back to Options
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Apply for {selectedCountry?.name || countryId} Visa</h1>
          <p className="text-gray-600">Complete your application and upload required documents</p>
        </div>

        <div className="bg-blue-100/50 border border-blue-200 rounded-lg p-4 mb-8 flex gap-3">
          <FaInfoCircle className="text-blue-500 mt-1 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-blue-900">Admin Customizable Form</h3>
            <p className="text-sm text-blue-800">This form is configured dynamically. Additional fields and country-specific document requests can be configured by the admin panel.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-bold flex items-center gap-2 text-gray-800">
              <span className="bg-blue-600 text-white w-6 h-6 flex justify-center items-center rounded-full text-sm">1</span>
              Primary Applicant
            </h2>
          </div>

          <div className="p-6">
            <h3 className="font-semibold mb-4 text-gray-700">Personal Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Full Name (as per Passport) <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={primaryApplicant.fullName}
                  onChange={(e) => setPrimaryApplicant({ ...primaryApplicant, fullName: e.target.value.toUpperCase() })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Passport Number <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={primaryApplicant.passportNumber}
                  onChange={(e) => setPrimaryApplicant({ ...primaryApplicant, passportNumber: e.target.value.toUpperCase() })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                  placeholder="A1234567"
                  required
                />
              </div>
            </div>

            <h3 className="font-semibold mb-4 text-gray-700">Required Documents</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              <FileUploadBox title="Passport Front" docKey="passportFront" required />
              <FileUploadBox title="Passport Back" docKey="passportBack" required />
              <FileUploadBox title="Passport Cover Black" docKey="passportCoverBlack" />
              <FileUploadBox title="Recent Photograph" docKey="recentPhotograph" accept=".jpg,.jpeg,.png" required />
              <FileUploadBox title="PAN Card" docKey="panCard" required />
              <FileUploadBox title="Onward Flight Ticket" docKey="onwardFlightTicket" accept=".pdf" />
              <FileUploadBox title="Return Flight Ticket" docKey="returnFlightTicket" accept=".pdf" />
              <FileUploadBox title="Additional Visa Document 1" docKey="additionalVisaDocument1" />
              <FileUploadBox title="Additional Visa Document 2" docKey="additionalVisaDocument2" />
              <FileUploadBox title="Additional Visa Document 3" docKey="additionalVisaDocument3" />
              <FileUploadBox title="Additional Visa Document 4" docKey="additionalVisaDocument4" />
            </div>

            {familyMembers.length > 0 && (
              <div className="mb-8">
                <h3 className="font-semibold mb-4 text-gray-700">Family Members</h3>
                {familyMembers.map((member, idx) => (
                  <div key={idx} className="bg-gray-50 p-4 rounded-xl border border-gray-200 mb-4 relative">
                    <button type="button" onClick={() => removeFamilyMember(idx)} className="absolute top-4 right-4 text-red-500 hover:text-red-700">
                      <FaTrash />
                    </button>
                    <h4 className="font-medium text-sm mb-3">Family Member {idx + 1}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Full Name</label>
                        <input
                          type="text"
                          value={member.fullName}
                          onChange={(e) => handleFamilyChange(idx, 'fullName', e.target.value.toUpperCase())}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Passport Number</label>
                        <input
                          type="text"
                          value={member.passportNumber}
                          onChange={(e) => handleFamilyChange(idx, 'passportNumber', e.target.value.toUpperCase())}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div className="mt-4 border-t pt-4">
                      <h5 className="text-xs font-semibold text-gray-700 mb-3">Family Member Documents</h5>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <FamilyFileUploadBox index={idx} title="Passport Front" docKey="passportFront" required />
                        <FamilyFileUploadBox index={idx} title="Passport Back" docKey="passportBack" required />
                        <FamilyFileUploadBox index={idx} title="Passport Cover Black" docKey="passportCoverBlack" />
                        <FamilyFileUploadBox index={idx} title="Photograph" docKey="recentPhotograph" accept=".jpg,.jpeg,.png" required />
                        <FamilyFileUploadBox index={idx} title="PAN Card" docKey="panCard" required />
                        <FamilyFileUploadBox index={idx} title="Onward Flight Ticket" docKey="onwardFlightTicket" accept=".pdf" />
                        <FamilyFileUploadBox index={idx} title="Return Flight Ticket" docKey="returnFlightTicket" accept=".pdf" />
                        <FamilyFileUploadBox index={idx} title="Additional Doc 1" docKey="additionalVisaDocument1" />
                        <FamilyFileUploadBox index={idx} title="Additional Doc 2" docKey="additionalVisaDocument2" />
                        <FamilyFileUploadBox index={idx} title="Additional Doc 3" docKey="additionalVisaDocument3" />
                        <FamilyFileUploadBox index={idx} title="Additional Doc 4" docKey="additionalVisaDocument4" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-center mb-8">
              <button
                type="button"
                onClick={addFamilyMember}
                className="flex items-center gap-2 px-6 py-2 bg-blue-50 text-blue-600 rounded-full font-medium hover:bg-blue-100 transition-colors"
              >
                <FaPlus size={12} /> Add Family Member
              </button>
            </div>

          </div>

          <div className="bg-gray-50 px-6 py-5 border-t border-gray-200">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 px-4 rounded-xl shadow-lg shadow-blue-200 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? "Submitting Application..." : "Submit Visa Application"}
            </button>
            <p className="text-center text-xs text-gray-500 mt-3">
              By submitting this form, you agree to our Terms of Service and Privacy Policy. Your documents are securely encrypted.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VisaApplicationForm;
